import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Clock, CheckCircle, XCircle, ChevronRight,
  ChevronLeft, Flag, BarChart3, RefreshCw, AlertCircle,
  Zap, Trophy, Lightbulb
} from "lucide-react";

import { API_BASE } from '@/services/api';

/**
 * Banking MCQ Practice Page
 * Fetches real exam-standard questions from backend
 */
const BankingPracticePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  const section = searchParams.get('section') || 'quant';
  const topic = searchParams.get('topic');
  const mode = searchParams.get('mode') || 'topic';
  const difficulty = searchParams.get('difficulty');
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [startTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [generating, setGenerating] = useState(false);

  const TOPIC_NAMES = {
    'number-series': 'Number Series',
    'simplification': 'Simplification & Approximation',
    'data-interpretation': 'Data Interpretation',
    'quadratic': 'Quadratic Equations',
    'arithmetic': 'Arithmetic',
    'time-work': 'Time & Work',
    'speed-distance': 'Speed & Distance',
    'probability': 'Probability & P&C',
    'average': 'Average',
    'ratio-proportion': 'Ratio & Proportion',
    'mixture-alligation': 'Mixture & Alligation',
    'mensuration': 'Mensuration',
    'number-system': 'Number System',
    'inequalities': 'Inequalities',
    'seating-linear': 'Linear Seating Arrangement',
    'seating-circular': 'Circular Seating',
    'puzzles': 'Puzzles',
    'syllogism': 'Syllogism',
    'blood-relations': 'Blood Relations',
    'direction': 'Direction Sense',
    'coding-decoding': 'Coding-Decoding',
    'inequality': 'Inequality',
    'input-output': 'Input-Output',
    'data-sufficiency': 'Data Sufficiency',
    'alphanumeric': 'Alphanumeric Series',
    'order-ranking': 'Order & Ranking',
    'reading-comprehension': 'Reading Comprehension',
    'cloze-test': 'Cloze Test',
    'error-spotting': 'Error Spotting',
    'sentence-improvement': 'Sentence Improvement',
    'para-jumbles': 'Para Jumbles',
    'fill-blanks': 'Fill in the Blanks',
    'vocabulary': 'Vocabulary',
    'sentence-rearrangement': 'Sentence Rearrangement',
    'phrase-replacement': 'Phrase Replacement',
  };

  useEffect(() => {
    loadQuestions();
  }, [topic, section, difficulty]);

  useEffect(() => {
    if (timeLeft > 0 && !sessionComplete && questions.length > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && questions.length > 0) {
      handleSubmit();
    }
  }, [timeLeft, sessionComplete, questions.length]);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE}/banking/questions/practice?section=${section}&limit=20`;
      if (topic) url += `&topic=${topic}`;
      if (difficulty) url += `&difficulty=${difficulty}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.questions && data.questions.length > 0) {
        // Normalize questions from Universal MCQ Bank format
        const normalized = data.questions.map(q => {
          const opts = (q.options || []).map(o => typeof o === 'object' ? (o.text || '') : o);
          // Convert letter-based correct_answer (e.g. "b") to numeric index
          let correctIdx = 0;
          if (q.correct_answer && typeof q.correct_answer === 'string') {
            const letter = q.correct_answer.toLowerCase().trim();
            const letterIdx = letter.charCodeAt(0) - 97; // a=0, b=1, c=2, d=3
            if (letterIdx >= 0 && letterIdx < opts.length) {
              correctIdx = letterIdx;
            }
          } else if (typeof q.correct === 'number') {
            correctIdx = q.correct;
          }
          return { ...q, options: opts, correct: correctIdx };
        });
        setQuestions(normalized);
      } else {
        setQuestions([]);
        setError("No questions available for this topic. Click 'Generate Questions' to create exam-standard questions.");
      }
    } catch (err) {
      console.error("Failed to load questions:", err);
      setError("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const generateQuestions = async () => {
    setGenerating(true);
    setError(null);
    try {
      // Use AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      const res = await fetch(`${API_BASE}/banking/questions/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          section: section,
          topic: topic,
          count: 25,
          difficulty_mix: true
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      const data = await res.json();
      
      if (data.success && data.generated > 0) {
        // Reload questions
        await loadQuestions();
      } else if (data.message) {
        setError(data.message);
      } else {
        setError("Could not generate questions. Please try again.");
      }
    } catch (err) {
      console.error("Failed to generate questions:", err);
      if (err.name === 'AbortError') {
        setError("Generation timed out. Please try again - it may take up to 60 seconds.");
      } else {
        setError("Failed to generate questions. Please check your connection and try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleNext = () => {
    if (selectedAnswer !== null) {
      setAnswers(prev => ({ ...prev, [currentIndex]: selectedAnswer }));
    }
    
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(answers[currentIndex + 1] ?? null);
      setShowResult(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      if (selectedAnswer !== null) {
        setAnswers(prev => ({ ...prev, [currentIndex]: selectedAnswer }));
      }
      setCurrentIndex(prev => prev - 1);
      setSelectedAnswer(answers[currentIndex - 1] ?? null);
      setShowResult(false);
    }
  };

  const handleCheckAnswer = () => {
    if (selectedAnswer !== null) {
      setAnswers(prev => ({ ...prev, [currentIndex]: selectedAnswer }));
      setShowResult(true);
    }
  };

  const handleSubmit = async () => {
    const finalAnswers = { ...answers };
    if (selectedAnswer !== null) {
      finalAnswers[currentIndex] = selectedAnswer;
    }
    
    let correct = 0;
    let wrong = 0;
    
    questions.forEach((q, idx) => {
      if (finalAnswers[idx] !== undefined) {
        if (finalAnswers[idx] === q.correct) {
          correct++;
        } else {
          wrong++;
        }
      }
    });
    
    const attempted = Object.keys(finalAnswers).length;
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    
    setResults({
      correct,
      wrong,
      attempted,
      total: questions.length,
      accuracy: attempted > 0 ? Math.round((correct / attempted) * 100) : 0,
      timeTaken,
      negativeMarks: wrong * 0.25,
      netScore: correct - (wrong * 0.25)
    });
    
    setSessionComplete(true);
    
    // Submit to backend
    try {
      await fetch(`${API_BASE}/banking/practice/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          section,
          topic,
          questions_attempted: attempted,
          correct,
          wrong,
          time_taken_seconds: timeTaken,
          difficulty: 'mixed'
        })
      });
    } catch (err) {
      console.error('Failed to submit practice:', err);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyBadge = (diff) => {
    const colors = {
      'easy': 'bg-emerald-100 text-emerald-700',
      'medium': 'bg-amber-100 text-amber-700',
      'hard': 'bg-red-100 text-red-700',
      'ultra-hard': 'bg-purple-100 text-purple-700'
    };
    return colors[diff] || 'bg-slate-100 text-slate-700';
  };

  const getTopicName = () => {
    return TOPIC_NAMES[topic] || topic || 'Mixed Practice';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-emerald-500 mb-4" />
          <p className="text-slate-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  // Error state / No questions - show generate option
  if (error || questions.length === 0) {
    const isGenerationError = error && error.includes("Failed") || error && error.includes("timed out");
    
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-md mx-auto mt-20">
          <Card>
            <CardContent className="p-6 text-center">
              <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${isGenerationError ? 'text-red-500' : 'text-amber-500'}`} />
              <h2 className="text-lg font-bold text-slate-900 mb-2">
                {isGenerationError ? "Generation Failed" : (error || "No Questions Available")}
              </h2>
              <p className="text-slate-600 mb-6">
                {isGenerationError 
                  ? "The AI generation timed out or failed. This can happen during peak hours. Please try again."
                  : `Generate exam-standard questions for ${getTopicName()} with AI. Questions will range from Easy to Ultra-Hard.`
                }
              </p>
              <div className="space-y-3">
                {isGenerationError ? (
                  <>
                    <Button
                      onClick={() => {
                        setError(null);
                        loadQuestions();
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Loading Again
                    </Button>
                    <Button
                      onClick={generateQuestions}
                      disabled={generating}
                      variant="outline"
                      className="w-full"
                    >
                      {generating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Retry Generation
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={generateQuestions}
                    disabled={generating}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating 25 Questions...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Generate Exam Questions
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate(`/banking/${section}`)}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to {section.charAt(0).toUpperCase() + section.slice(1)}
                </Button>
              </div>
              {generating && (
                <p className="text-xs text-slate-500 mt-4">
                  This may take 30-60 seconds. Generating Easy, Medium, Hard & Ultra-Hard questions...
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Session complete - show results
  if (sessionComplete && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-4">
        <div className="max-w-2xl mx-auto">
          <Card className="mt-8">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  results.accuracy >= 70 ? 'bg-emerald-100' : results.accuracy >= 50 ? 'bg-amber-100' : 'bg-red-100'
                }`}>
                  {results.accuracy >= 70 ? (
                    <Trophy className="w-10 h-10 text-emerald-600" />
                  ) : (
                    <BarChart3 className={`w-10 h-10 ${results.accuracy >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Practice Complete!</h2>
                <p className="text-slate-500">{getTopicName()}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{results.correct}</p>
                  <p className="text-xs text-slate-500">Correct</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{results.wrong}</p>
                  <p className="text-xs text-slate-500">Wrong</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{results.accuracy}%</p>
                  <p className="text-xs text-slate-500">Accuracy</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{formatTime(results.timeTaken)}</p>
                  <p className="text-xs text-slate-500">Time</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Net Score (with negative marking)</span>
                  <span className="font-bold text-lg">{results.netScore.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-500">
                  <span>Negative marks: -{results.negativeMarks.toFixed(2)}</span>
                  <span>{results.attempted}/{results.total} attempted</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`/banking/${section}`)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    setSessionComplete(false);
                    setCurrentIndex(0);
                    setAnswers({});
                    setSelectedAnswer(null);
                    setShowResult(false);
                    setTimeLeft(1200);
                    loadQuestions();
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Practice Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  // Main practice interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/banking/${section}`)}
                className="bg-white/10 hover:bg-white/20 text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold">{getTopicName()}</h1>
                <p className="text-xs text-emerald-200">Q {currentIndex + 1} of {questions.length}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Badge className={getDifficultyBadge(currentQuestion?.difficulty)}>
                {currentQuestion?.difficulty?.toUpperCase()}
              </Badge>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                timeLeft < 60 ? 'bg-red-500' : 'bg-white/20'
              }`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
              </div>
              <Button
                onClick={handleSubmit}
                className="bg-white/20 hover:bg-white/30 text-white"
                size="sm"
              >
                <Flag className="w-4 h-4 mr-1" />
                Submit
              </Button>
            </div>
          </div>
          
          <div className="mt-4">
            <Progress value={((currentIndex + 1) / questions.length) * 100} className="h-2" />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <Badge variant="outline">Q{currentIndex + 1}</Badge>
              {showResult && (
                <Badge className={selectedAnswer === currentQuestion?.correct ? 'bg-emerald-500' : 'bg-red-500'}>
                  {selectedAnswer === currentQuestion?.correct ? 'Correct!' : 'Wrong'}
                </Badge>
              )}
            </div>
            
            <p className="text-lg text-slate-900 mb-6 whitespace-pre-wrap">{currentQuestion?.question}</p>
            
            <div className="space-y-3">
              {currentQuestion?.options?.map((option, idx) => (
                <button
                  key={idx}
                  data-testid={`option-${idx}`}
                  onClick={() => handleAnswerSelect(idx)}
                  disabled={showResult}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    showResult
                      ? idx === currentQuestion.correct
                        ? 'border-emerald-500 bg-emerald-50'
                        : idx === selectedAnswer
                          ? 'border-red-500 bg-red-50'
                          : 'border-slate-200'
                      : selectedAnswer === idx
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      showResult
                        ? idx === currentQuestion.correct
                          ? 'bg-emerald-500 text-white'
                          : idx === selectedAnswer
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-200 text-slate-600'
                        : selectedAnswer === idx
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-600'
                    }`}>
                      {String.fromCharCode(65 + idx)}
                    </div>
                    <span className="text-slate-700 flex-1">{option}</span>
                    {showResult && idx === currentQuestion.correct && (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                    {showResult && idx === selectedAnswer && idx !== currentQuestion.correct && (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {showResult && currentQuestion?.explanation && (
              <div className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-blue-900">Detailed Solution</h3>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-blue-100">
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {currentQuestion.explanation.split('\n').map((line, idx) => {
                      // Style step numbers
                      if (line.match(/^(Step \d+|STEP \d+)/i)) {
                        return (
                          <p key={idx} className="font-bold text-blue-800 mt-3 mb-1">
                            {line}
                          </p>
                        );
                      }
                      // Style conclusions
                      if (line.match(/^(Therefore|Hence|Thus|Answer|Correct Answer)/i)) {
                        return (
                          <p key={idx} className="font-semibold text-emerald-700 mt-3 bg-emerald-50 p-2 rounded">
                            ✓ {line}
                          </p>
                        );
                      }
                      // Style pattern identification
                      if (line.match(/^(Pattern|Rule|Formula)/i)) {
                        return (
                          <p key={idx} className="font-medium text-indigo-700 mt-2 bg-indigo-50 p-2 rounded">
                            📐 {line}
                          </p>
                        );
                      }
                      return <p key={idx} className="mb-1">{line}</p>;
                    })}
                  </div>
                </div>
                
                {/* Quick Tip */}
                <div className="mt-3 flex items-start gap-2 text-xs text-blue-600">
                  <span className="bg-blue-100 px-2 py-0.5 rounded font-medium">Pro Tip</span>
                  <span>In exams, identify the pattern by checking differences or ratios between consecutive terms first.</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          
          {!showResult && (
            <Button
              onClick={handleCheckAnswer}
              disabled={selectedAnswer === null}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Check Answer
            </Button>
          )}
          
          {currentIndex < questions.length - 1 ? (
            <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Finish
              <CheckCircle className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Question Navigator */}
        <div className="mt-6 p-4 bg-white rounded-xl border">
          <p className="text-sm text-slate-500 mb-3">Question Navigator:</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (selectedAnswer !== null) {
                    setAnswers(prev => ({ ...prev, [currentIndex]: selectedAnswer }));
                  }
                  setCurrentIndex(idx);
                  setSelectedAnswer(answers[idx] ?? null);
                  setShowResult(false);
                }}
                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors relative ${
                  idx === currentIndex
                    ? 'bg-emerald-500 text-white'
                    : answers[idx] !== undefined
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                title={q.difficulty}
              >
                {idx + 1}
                {/* Difficulty indicator dot */}
                <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                  q.difficulty === 'easy' ? 'bg-emerald-400' :
                  q.difficulty === 'medium' ? 'bg-amber-400' :
                  q.difficulty === 'hard' ? 'bg-red-400' :
                  'bg-purple-400'
                }`} />
              </button>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Easy</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Medium</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Hard</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" /> Ultra-Hard</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankingPracticePage;
